import { defineStore } from 'pinia';
import { kv } from '@/services/platform';
import { uid } from '@/shared/lib/uid';

export type ThemePref = 'light' | 'dark' | 'auto';
export interface Toast {
  id: string;
  text: string;
  kind: 'success' | 'info' | 'error';
  actionLabel?: string;
  onAction?: () => void;
}

const THEME_KEY = 'td.ui.theme';

interface State {
  theme: ThemePref;
  childMode: boolean;
  toasts: Toast[];
}

function systemDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export const useUiStore = defineStore('ui', {
  state: (): State => ({
    theme: 'light',
    childMode: false,
    toasts: [],
  }),
  getters: {
    resolvedDark(s): boolean {
      return s.theme === 'auto' ? systemDark() : s.theme === 'dark';
    },
  },
  actions: {
    async init() {
      const saved = (await kv.get(THEME_KEY)) as ThemePref | null;
      if (saved === 'light' || saved === 'dark' || saved === 'auto') this.theme = saved;
      this.applyTheme();
      window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
        if (this.theme === 'auto') this.applyTheme();
      });
    },
    applyTheme() {
      document.documentElement.classList.toggle('dark', this.resolvedDark);
    },
    async setTheme(t: ThemePref) {
      this.theme = t;
      await kv.set(THEME_KEY, t);
      this.applyTheme();
    },
    /** child-режим: 56dp + всегда число+кольцо (§2/§6.6). Драйвится ролью. */
    setChildMode(on: boolean) {
      this.childMode = on;
      document.documentElement.setAttribute('data-child', on ? 'on' : 'off');
    },
    toast(
      text: string,
      kind: Toast['kind'] = 'success',
      opts?: { actionLabel?: string; onAction?: () => void; timeout?: number },
    ): string {
      const id = uid();
      this.toasts.push({ id, text, kind, actionLabel: opts?.actionLabel, onAction: opts?.onAction });
      const ms = opts?.timeout ?? 3200;
      if (ms > 0) setTimeout(() => this.dismissToast(id), ms);
      return id;
    },
    dismissToast(id: string) {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    },
  },
});
